<script setup>
import { ref, onMounted, computed } from 'vue';
import { Switch } from '@headlessui/vue';
import AppLoader from '../../components/AppLoader.vue';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid';
import { useNovuStore } from '@/stores/novu.store';
import { novu_alert_channels, novu_alert_channel_icons } from '../../utils/utils.js';
import HalfCircleSpinner from '../global/loader/HalfCircleSpinner.vue';

const loading = ref(true);
const userPreferences = ref([]);
// Track individual toggle loading states using a unique key per template-channel.
const toggleLoading = ref({});
const novuStore = useNovuStore();

onMounted(async () => {
    const fetchedUserPreferences = await novuStore.fetchUserPreferences();
    if (fetchedUserPreferences && novuStore.userPreferences) {
        userPreferences.value = novuStore.userPreferences.map((preference) => ({
            ...preference,
            expanded: false
        }));
    }
    loading.value = false;
});

function resolveChannelName(channelCode) {
    return novu_alert_channels[channelCode] || channelCode;
}

function resolveChannelIcon(channelCode) {
    return novu_alert_channel_icons[channelCode] || channelCode;
}

function getChannelNames(channels) {
    return Object.entries(channels)
        .filter(([_, enabled]) => enabled)
        .map(([channel]) => resolveChannelName(channel))
        .join(', ');
}

function toggleTemplate(templateId) {
    userPreferences.value = userPreferences.value.map((pref) => {
        if (pref.template._id === templateId) {
            return { ...pref, expanded: !pref.expanded };
        }
        return pref;
    });
}

const sortedTemplates = computed(() => {
    return userPreferences.value
        .filter((item) => item.preference.enabled)
        .map((item) => ({
            id: item.template._id,
            name: item.template.name,
            channels: item.preference.channels,
            expanded: item.expanded
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

async function toggleChannelStatus(templateId, channelType) {
    const template = userPreferences.value.find((pref) => pref.template._id === templateId);
    if (!template) return;
    const currentValue = template.preference.channels[channelType];
    const newValue = !currentValue;
    const key = `${templateId}-${channelType}`;

    // Set loading state for this toggle.
    toggleLoading.value[key] = true;
    try {
        const preference = {
            templateId,
            channelType,
            checked: newValue
        };

        const updated = await novuStore.updateUserPreference(preference);

        if (updated) {
            userPreferences.value = userPreferences.value.map((pref) => {
                if (pref.template._id === templateId) {
                    return {
                        ...pref,
                        preference: {
                            ...pref.preference,
                            channels: {
                                ...pref.preference.channels,
                                [channelType]: newValue
                            }
                        }
                    };
                }
                return pref;
            });
        } else {
            console.error('Failed to update channel status.');
        }
    } catch (error) {
        console.error('Error updating channel status:', error);
    } finally {
        toggleLoading.value[key] = false;
    }
}
</script>

<template>
    <div v-if="loading" class="table-loader min-h-[50px] mt-2">
        <AppLoader :force-loading="loading" default-height="40vh" />
    </div>
    <div v-else class="integrations-list overflow-auto max-h-[60vh] px-2 py-4">
        <div v-for="template in sortedTemplates" :key="template.id" class="mb-[16px]">
            <div class="integration-item bg-white shadow-sm rounded overflow-hidden">
                <!-- Header Row -->
                <div class="flex items-center justify-between p-4 cursor-pointer" @click="toggleTemplate(template.id)">
                    <div>
                        <div class="flex items-center">
                            <span class="text-md font-medium">{{ template.name }}</span>
                        </div>
                        <div class="flex mt-1">
                            <div
                                v-for="channel in Object.keys(template.channels).filter((c) => template.channels[c])"
                                :key="channel"
                                class="flex items-center mr-2">
                                <span
                                    :class="`mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[0.6rem] font-medium bg-green-100 text-green-800 ring-green-600/20 ring-1 ring-inset ring-red-600/20`">
                                    {{ resolveChannelName(channel) }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <ChevronDownIcon v-if="!template.expanded" class="-mr-1 ml-2 h-5 w-5" />
                        <ChevronUpIcon v-else class="-mr-1 ml-2 h-5 w-5" />
                    </div>
                </div>
                <!-- Expanded Toggles Row -->
                <div v-if="template.expanded" class="px-4 py-2">
                    <hr class="mb-4" />
                    <div
                        v-for="(enabled, channel) in template.channels"
                        :key="`channel-${channel}`"
                        class="flex items-center justify-between py-2 border-b last:border-b-0">
                        <div class="flex items-center text-sm">
                            <img :src="resolveChannelIcon(channel)" class="w-8 h-8 mr-4 opacity-60" />
                            <div class="self-center">{{ resolveChannelName(channel) }}</div>
                        </div>
                        <div class="flex items-center">
                            <HalfCircleSpinner v-if="toggleLoading[`${template.id}-${channel}`]" :loading="true" class="mr-2" />
                            <Switch
                                :checked="template.channels[channel]"
                                :disabled="toggleLoading[`${template.id}-${channel}`]"
                                @click="toggleChannelStatus(template.id, channel)"
                                :class="[
                                    template.channels[channel] ? 'bg-green-400' : 'bg-neutral-300',
                                    'relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out'
                                ]"
                                :true-value="true"
                                :false-value="false"
                                aria-label="input"
                                aria-describedby="toggle">
                                <span
                                    aria-hidden="true"
                                    :class="[
                                        template.channels[channel] ? 'translate-x-5' : 'translate-x-0',
                                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out'
                                    ]" />
                            </Switch>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
